import {
  ApartmentOutlined,
  AppstoreOutlined,
  AccountBookOutlined,
  AuditOutlined,
  BankOutlined,
  BarChartOutlined,
  LineChartOutlined,
  BookOutlined,
  CalendarOutlined,
  CarOutlined,
  CloudUploadOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  DownOutlined,
  EnvironmentOutlined,
  FileDoneOutlined,
  FileProtectOutlined,
  FileSyncOutlined,
  SwapOutlined,
  FileTextOutlined,
  GlobalOutlined,
  InboxOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MenuUnfoldOutlined,
  NodeIndexOutlined,
  ProfileOutlined,
  SafetyCertificateOutlined,
  SafetyOutlined,
  SettingOutlined,
  TeamOutlined,
  UserOutlined,
  UsergroupAddOutlined,
  DiffOutlined,
  ClusterOutlined,
  FileSearchOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Avatar,
  Button,
  Drawer,
  Dropdown,
  Flex,
  Grid,
  Layout,
  Menu,
  Select,
  Skeleton,
  Tag,
  Typography,
} from 'antd';
import type { MenuProps } from 'antd';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useSiteContext } from '../context/SiteContext';
import { hasStoredLocale, setLocale, type AppLocale } from '../i18n';
import { LANGUAGES, ProfileDrawer, useSaveLocale } from './ProfileDrawer';
import { NotificationBell } from './Notifications';
import { HelpMenu } from '../components/HelpMenu';
import { HelpProvider } from '../help/HelpCenter';
import { BrandMark } from '../components/BrandMark';
import { brand, neutral } from '../theme';
import { ThemeToggle } from '../themeMode';

const { Header, Sider, Content } = Layout;

/**
 * The mark and name of the application at the top of the navigation pane: as tall as the header
 * and closed by the same stroke, so that both form one band, as on Microsoft's sites.
 */
export function Logo({ collapsed, bordered = true }: { collapsed: boolean; bordered?: boolean }) {
  const { t } = useTranslation();
  return (
    <Flex
      align="center"
      gap={10}
      className="wm-logo"
      style={{
        height: 52,
        flex: 'none',
        padding: collapsed ? 0 : '0 16px',
        justifyContent: collapsed ? 'center' : 'flex-start',
        borderBottom: bordered ? `1px solid ${neutral.stroke2}` : undefined,
      }}
    >
      <BrandMark size={28} />
      {!collapsed && (
        <div style={{ lineHeight: 1.2, minWidth: 0 }}>
          <div
            style={{
              color: neutral.foreground1,
              fontWeight: 600,
              fontSize: 14,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {t('app.brand')}
          </div>
          <div style={{ color: neutral.foreground3, fontSize: 12 }}>{t('app.tagline')}</div>
        </div>
      )}
    </Flex>
  );
}

export function AppShell() {
  const { t, i18n } = useTranslation();
  const { user, signOut } = useAuth();
  const { me, sites, site, setSiteId, can } = useSiteContext();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = Grid.useBreakpoint();
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const saveLocale = useSaveLocale();

  // a link from an alert or an e-mail names the site of the object it opens
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const wanted = params.get('site');
    if (!wanted) return;
    if (sites.some((s) => s.id === wanted) && wanted !== site?.id) setSiteId(wanted);
    params.delete('site');
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, sites]);

  // on a new device, the interface opens in the language of the user's profile
  useEffect(() => {
    const locale = me?.principal.locale;
    if (locale && !hasStoredLocale() && locale !== i18n.language.slice(0, 2)) setLocale(locale);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.principal.locale]);

  const canImport =
    can.configureSite ||
    can.administerSite ||
    can.administerTenant ||
    can.administerUsers ||
    can.manageCosts ||
    can.createOperations;
  const items: MenuProps['items'] = useMemo(
    () => [
      { key: '/', icon: <DashboardOutlined />, label: t('nav.dashboard') },
      ...(can.group
        ? [
            { key: '/group', icon: <ClusterOutlined />, label: t('nav.group') },
            {
              key: '/group/registre',
              icon: <FileSearchOutlined />,
              label: t('nav.groupRegistre'),
            },
            { key: '/group/analyses', icon: <LineChartOutlined />, label: t('nav.groupAnalyses') },
          ]
        : []),
      {
        type: 'group',
        label: t('nav.operations'),
        children: [
          { key: '/pickup-requests', icon: <AppstoreOutlined />, label: t('nav.pickupRequests') },
          { key: '/expeditions', icon: <CarOutlined />, label: t('nav.expeditions') },
          { key: '/returns', icon: <FileSyncOutlined />, label: t('nav.returns') },
          { key: '/transfers', icon: <SwapOutlined />, label: t('nav.transfers') },
          { key: '/stock', icon: <DatabaseOutlined />, label: t('nav.stock') },
          { key: '/registre', icon: <FileProtectOutlined />, label: t('nav.registre') },
          { key: '/annual-report', icon: <FileTextOutlined />, label: t('nav.annualReport') },
          { key: '/compliance', icon: <SafetyOutlined />, label: t('nav.compliance') },
          { key: '/calendar', icon: <CalendarOutlined />, label: t('nav.calendar') },
          { key: '/indicators', icon: <GlobalOutlined />, label: t('nav.indicators') },
          { key: '/analyses', icon: <LineChartOutlined />, label: t('nav.analyses') },
        ],
      },
      {
        type: 'group',
        label: t('nav.referentials'),
        children: [
          { key: '/wastes', icon: <DeleteOutlined />, label: t('nav.wastes') },
          { key: '/filieres', icon: <NodeIndexOutlined />, label: t('nav.routes') },
          { key: '/conditionnements', icon: <InboxOutlined />, label: t('nav.conditionnements') },
          { key: '/prestataires', icon: <TeamOutlined />, label: t('nav.prestataires') },
          { key: '/caps', icon: <SafetyCertificateOutlined />, label: t('nav.caps') },
          { key: '/other-documents', icon: <ProfileOutlined />, label: t('nav.otherDocuments') },
          ...(can.contracts
            ? [
                { key: '/contracts', icon: <FileDoneOutlined />, label: t('nav.contracts') },
                { key: '/invoices', icon: <AccountBookOutlined />, label: t('nav.invoices') },
              ]
            : []),
          ...(canImport
            ? [{ key: '/imports', icon: <CloudUploadOutlined />, label: t('nav.imports') }]
            : []),
        ],
      },
      {
        type: 'group',
        label: t('nav.settings'),
        children: [
          { key: '/settings/site', icon: <SettingOutlined />, label: t('nav.sitePreferences') },
          { key: '/settings/origins', icon: <ApartmentOutlined />, label: t('nav.origins') },
          { key: '/settings/activity', icon: <BarChartOutlined />, label: t('nav.activity') },
          { key: '/ref/cmd', icon: <BookOutlined />, label: t('nav.catalogue') },
          { key: '/ref/impacts', icon: <DiffOutlined />, label: t('nav.refImpacts') },
        ],
      },
      ...(can.administerUsers
        ? [
            {
              type: 'group' as const,
              label: t('nav.administration'),
              children: [
                ...(can.administerTenant
                  ? [
                      {
                        key: '/admin/organisation',
                        icon: <BankOutlined />,
                        label: t('nav.organisation'),
                      },
                    ]
                  : []),
                { key: '/admin/users', icon: <UsergroupAddOutlined />, label: t('nav.users') },
                { key: '/admin/audit', icon: <AuditOutlined />, label: t('nav.audit') },
              ],
            },
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, can.administerTenant, can.administerUsers, can.contracts, can.group, canImport],
  );

  const selectedKey = useMemo(() => {
    const keys = [
      '/group/analyses',
      '/group/registre',
      '/group',
      '/pickup-requests',
      '/expeditions',
      '/returns',
      '/chargements',
      '/stock',
      '/registre',
      '/annual-report',
      '/compliance',
      '/indicators',
      '/analyses',
      '/wastes',
      '/filieres',
      '/conditionnements',
      '/prestataires',
      '/caps',
      '/other-documents',
      '/contracts',
      '/imports',
      '/settings/site',
      '/settings/origins',
      '/settings/activity',
      '/ref/cmd',
      '/ref/impacts',
      '/admin/organisation',
      '/admin/users',
      '/admin/audit',
    ];
    const key = keys.find((k) => location.pathname.startsWith(k)) ?? '/';
    return key === '/chargements' ? '/expeditions' : key;
  }, [location.pathname]);

  const siteOptions = useMemo(() => {
    const byCompany = new Map<
      string,
      { label: string; options: { value: string; label: React.ReactNode; search: string }[] }
    >();
    for (const s of sites) {
      const key = s.companyId;
      if (!byCompany.has(key)) byCompany.set(key, { label: s.companyName ?? '—', options: [] });
      byCompany.get(key)!.options.push({
        value: s.id,
        search: `${s.name} ${s.city ?? ''}`,
        label: (
          <Flex justify="space-between" gap={12}>
            <span>{s.name}</span>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {s.city}
            </Typography.Text>
          </Flex>
        ),
      });
    }
    return [...byCompany.values()];
  }, [sites]);

  const displayName =
    me?.principal.displayName ?? user?.profile.name ?? user?.profile.preferred_username ?? '';
  const initials = displayName
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const userMenu: MenuProps = {
    items: [
      {
        key: 'who',
        disabled: true,
        label: (
          <div style={{ padding: '4px 0', maxWidth: 260 }}>
            <div style={{ fontWeight: 600, color: neutral.foreground1 }}>{displayName}</div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {me?.principal.email}
            </Typography.Text>
            <div style={{ marginTop: 6 }}>
              {(site?.roles ?? []).map((r) => (
                <Tag key={r} color="cyan" style={{ marginBottom: 4 }}>
                  {t(`roles.${r}`)}
                </Tag>
              ))}
            </div>
          </div>
        ),
      },
      { type: 'divider' },
      {
        key: 'profile',
        icon: <UserOutlined />,
        label: t('profile.title'),
        onClick: () => setProfileOpen(true),
      },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: t('app.signOut'),
        onClick: () => void signOut(),
      },
    ],
  };

  const mobile = !screens.md;
  const rtl = i18n.dir() === 'rtl';
  const languages = LANGUAGES;

  const navigation = (compact: boolean) => (
    <Flex vertical style={{ height: '100%' }}>
      <Logo collapsed={compact} />
      <Menu
        className="wm-nav"
        theme="light"
        mode="inline"
        selectedKeys={[selectedKey]}
        items={items}
        inlineCollapsed={compact}
        onClick={({ key }) => {
          navigate(key);
          setDrawerOpen(false);
        }}
        style={{ borderInlineEnd: 'none', flex: 1, overflowY: 'auto', paddingBlock: 8 }}
      />
      {!compact && me && (
        <div style={{ padding: '12px 16px', borderTop: `1px solid ${neutral.stroke2}` }}>
          <div style={{ color: neutral.foreground3, fontSize: 12, lineHeight: '16px' }}>
            {t('shell.tenant')}
          </div>
          <div style={{ color: neutral.foreground1, fontSize: 14, fontWeight: 600, marginTop: 2 }}>
            {me.tenant.name}
          </div>
        </div>
      )}
    </Flex>
  );

  return (
    <HelpProvider>
      <Layout style={{ minHeight: '100vh' }}>
        {mobile ? (
          <Drawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            placement={rtl ? 'right' : 'left'}
            size={272}
            closable={false}
            styles={{
              body: { padding: 0, background: neutral.background1 },
              header: { display: 'none' },
            }}
          >
            {navigation(false)}
          </Drawer>
        ) : (
          <Sider
            data-tour="nav"
            theme="light"
            width={260}
            collapsedWidth={64}
            collapsed={collapsed}
            breakpoint="lg"
            onBreakpoint={(broken) => setCollapsed(broken)}
            trigger={null}
            style={{
              position: 'sticky',
              top: 0,
              height: '100vh',
              overflow: 'hidden',
              borderInlineEnd: `1px solid ${neutral.stroke2}`,
            }}
          >
            {navigation(collapsed)}
          </Sider>
        )}
        <Layout style={{ minWidth: 0 }}>
          <Header
            style={{
              borderBottom: `1px solid ${neutral.stroke2}`,
              display: 'flex',
              alignItems: 'center',
              gap: mobile ? 4 : 8,
              padding: mobile ? '0 8px' : undefined,
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}
          >
            <Button
              type="text"
              icon={
                mobile ? (
                  <MenuOutlined />
                ) : collapsed ? (
                  <MenuUnfoldOutlined />
                ) : (
                  <MenuFoldOutlined />
                )
              }
              onClick={() => (mobile ? setDrawerOpen(true) : setCollapsed(!collapsed))}
              aria-label={t('shell.menu')}
            />
            <Flex data-tour="site" align="center" gap={8} style={{ flex: 1, minWidth: 0 }}>
              {!mobile && (
                <EnvironmentOutlined style={{ color: neutral.foreground3, fontSize: 16 }} />
              )}
              <Select
                variant="borderless"
                value={site?.id}
                onChange={setSiteId}
                options={siteOptions}
                showSearch={{ optionFilterProp: 'search' }}
                placeholder={t('shell.selectSite')}
                suffixIcon={<DownOutlined />}
                popupMatchSelectWidth={mobile ? 300 : 360}
                style={{
                  minWidth: 0,
                  width: mobile ? '100%' : undefined,
                  maxWidth: 380,
                  fontWeight: 600,
                }}
                labelRender={() => (
                  <Flex component="span" gap={8} align="baseline" style={{ minWidth: 0 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {site?.name}
                    </span>
                    {!mobile && site?.companyName && (
                      <Typography.Text type="secondary" style={{ fontWeight: 400, fontSize: 12 }}>
                        {site.companyName}
                      </Typography.Text>
                    )}
                  </Flex>
                )}
              />
            </Flex>
            <span data-tour="notifications" style={{ display: 'inline-flex' }}>
              <NotificationBell />
            </span>
            {mobile ? (
              <Dropdown
                trigger={['click']}
                menu={{
                  selectedKeys: [i18n.language.slice(0, 2)],
                  items: languages.map((l) => ({ key: l.value, label: l.label })),
                  onClick: ({ key }) => saveLocale(key as AppLocale),
                }}
              >
                <Button type="text" icon={<GlobalOutlined />} aria-label={t('shell.language')} />
              </Dropdown>
            ) : (
              <Select
                size="small"
                variant="borderless"
                value={i18n.language.slice(0, 2)}
                onChange={(v) => saveLocale(v as AppLocale)}
                prefix={<GlobalOutlined style={{ color: neutral.foreground3 }} />}
                options={languages}
                style={{ width: 132 }}
              />
            )}
            <ThemeToggle />
            <HelpMenu />
            <Dropdown menu={userMenu} trigger={['click']} placement="bottomRight">
              <Button
                data-tour="account"
                type="text"
                style={{ height: 40, paddingInline: 6, fontWeight: 400 }}
              >
                <Flex align="center" gap={8}>
                  <Avatar
                    size={32}
                    style={{ background: brand.background, fontWeight: 600, fontSize: 14 }}
                  >
                    {initials || '?'}
                  </Avatar>
                  {screens.lg && (
                    <div style={{ textAlign: 'start', lineHeight: 1.2 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{displayName}</div>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {site?.roles?.[0] ? t(`roles.${site.roles[0]}`) : ''}
                      </Typography.Text>
                    </div>
                  )}
                </Flex>
              </Button>
            </Dropdown>
          </Header>
          <Content
            className="wm-content"
            style={{ padding: mobile ? 16 : 24, maxWidth: 1440, width: '100%', margin: '0 auto' }}
          >
            {site && !site.active && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                title={t('shell.inactiveSite')}
              />
            )}
            <Suspense fallback={<Skeleton active paragraph={{ rows: 8 }} />}>
              <Outlet />
            </Suspense>
          </Content>
        </Layout>
        <ProfileDrawer open={profileOpen} onClose={() => setProfileOpen(false)} />
      </Layout>
    </HelpProvider>
  );
}
